"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

interface FloatingCard {
  id: string;
  image: string;
  alt: string;
  title: string;
  badge: string;
  position: string;
  delay: string;
}

export default function HeroSection() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const floatingCards: FloatingCard[] = [
  {
    id: "mineral_gold",
    image: "https://images.unsplash.com/photo-1634588513592-e34817f2e9e5",
    alt: "Raw gold mineral ore specimen with metallic golden yellow surface and natural crystalline structure",
    title: "Gold Ore",
    badge: "Ethically Sourced",
    position: "top-20 left-10",
    delay: "animate-delay-200"
  },
  {
    id: "mineral_cobalt",
    image: "https://images.unsplash.com/photo-1729372460899-d1f436483669",
    alt: "Blue cobalt mineral specimen with deep azure coloration and metallic luster on dark rock matrix",
    title: "Cobalt",
    badge: "Fair Trade",
    position: "top-40 right-20",
    delay: "animate-delay-400"
  },
  {
    id: "mineral_copper",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15a2cb2c6-1767060268769.png",
    alt: "Copper mineral ore with reddish-brown metallic surface and oxidized green patina areas",
    title: "Copper Ore",
    badge: "Traceable",
    position: "bottom-32 left-20",
    delay: "animate-delay-600"
  }];


  return (
    <section className="relative min-h-screen pt-32 pb-20 overflow-hidden bg-gradient-to-br from-background via-background to-muted/20">
      {/* Animated Grid Background */}
      <div className="hidden lg:grid fixed inset-0 grid-cols-8 pointer-events-none px-10 opacity-20">
        <div className="grid-line-v border-l border-primary/20 animate-pulse"></div>
        <div className="grid-line-v border-l border-golden/10"></div>
        <div className="grid-line-v border-l border-silver/10"></div>
        <div className="grid-line-v border-l border-primary/20"></div>
        <div className="grid-line-v border-l border-golden/10"></div>
        <div className="grid-line-v border-l border-silver/10"></div>
        <div className="grid-line-v border-l border-primary/20"></div>
        <div className="grid-line-v border-l border-golden/10"></div>
      </div>

      {/* Floating Orbs */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-golden/30 to-silver/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-tl from-primary/20 to-golden/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>

      <div className="container mx-auto px-4 relative z-10 max-w-7xl">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Left Content */}
          <div className="lg:w-1/2 text-left space-y-8">
            <div className="animate-in animate-delay-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-[2px] h-4 bg-gradient-to-b from-golden via-primary to-silver"></div>
                <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-transparent bg-clip-text bg-gradient-to-r from-golden to-silver font-mono">
                  Certified Ethical Mining
                </span>
              </div>
              <h1 className="uppercase leading-[0.9] text-5xl md:text-7xl lg:text-8xl font-semibold tracking-tight mb-6 font-heading">
                <span className="inline-block hover:scale-105 transition-transform">Ethical</span><br />
                <span className="inline-block hover:scale-105 transition-transform">Minerals</span><br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-golden via-silver to-golden font-light inline-block hover:scale-105 transition-transform">From Uganda</span>
              </h1>
            </div>

            <div className="animate-in animate-delay-300">
              <h2 className="text-xl md:text-2xl text-muted-foreground tracking-tight font-heading max-w-xl leading-relaxed">
                Transparent supply chains. Sustainable practices. Fair trade
                certified minerals for global industries.
              </h2>
            </div>

            <div className="animate-in animate-delay-500 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <Link
                href="/products"
                className="group relative inline-block bg-gradient-to-r from-golden via-primary to-silver text-foreground px-10 py-4 text-sm font-semibold tracking-widest uppercase transition-all hover:scale-105 hover:shadow-2xl hover:shadow-golden/50 overflow-hidden rounded-lg">
                
                <span className="relative z-10 font-mono">
                  Explore Products
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-silver via-primary to-golden opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </Link>

              <div className="flex items-center gap-3 py-2 px-4 border-2 border-golden/30 bg-card/80 backdrop-blur-md rounded-full shadow-lg hover:border-golden/60 transition-all">
                <div className="w-2 h-2 bg-gradient-to-r from-golden to-silver animate-pulse-slow rounded-full shadow-lg shadow-golden/50"></div>
                <span className="text-[10px] uppercase tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-golden to-silver font-mono">
                  100% Traceable Supply Chain
                </span>
              </div>
            </div>
          </div>

          {/* Right Content - Floating Cards */}
          <div className="lg:w-1/2 relative min-h-[500px] w-full">
            {isHydrated &&
            floatingCards.map((card) =>
            <div
              key={card.id}
              className={`absolute ${card.position} w-64 animate-in ${card.delay} animate-float`}>
              
                  <div className="bg-card/90 backdrop-blur-md border border-border p-4 shadow-2xl rounded-lg hover:border-primary/30 transition-all duration-500 group">
                    <div className="relative overflow-hidden rounded-md mb-3 h-40">
                      <AppImage
                    src={card.image}
                    alt={card.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  
                      <div className="absolute top-2 right-2 bg-success/90 backdrop-blur-sm px-2 py-1 text-[8px] uppercase tracking-wider text-success-foreground font-mono">
                        {card.badge}
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground font-heading">
                      {card.title}
                    </h3>
                  </div>
                </div>
            )}

            {/* Decorative blur */}
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/10 blur-[120px]"></div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 border-y-2 border-golden/20 bg-gradient-to-r from-card/50 via-card/80 to-card/50 backdrop-blur-md animate-in animate-delay-600 shadow-xl">
          <div className="p-8 border-r-2 border-golden/20 flex items-center gap-6 group hover:bg-gradient-to-br hover:from-golden/10 hover:to-silver/10 transition-all duration-500">
            <div className="w-14 h-14 bg-gradient-to-br from-golden to-silver rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <Icon
                name="CheckBadgeIcon"
                size={28}
                className="text-foreground flex-shrink-0" />
            </div>
            <div>
              <div className="text-3xl text-transparent bg-clip-text bg-gradient-to-r from-golden to-silver tracking-tighter font-heading font-light">
                ISO 14001
              </div>
              <h3 className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
                Environmental Certified
              </h3>
            </div>
          </div>

          <div className="p-8 border-r-2 border-silver/20 flex items-center gap-6 group hover:bg-gradient-to-br hover:from-silver/10 hover:to-golden/10 transition-all duration-500">
            <div className="w-14 h-14 bg-gradient-to-br from-silver to-golden rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <Icon
                name="GlobeAltIcon"
                size={28}
                className="text-foreground flex-shrink-0" />
            </div>
            <div>
              <div className="text-3xl text-transparent bg-clip-text bg-gradient-to-r from-silver to-golden tracking-tighter font-heading font-light">
                30+ Countries
              </div>
              <h3 className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
                Global Export Network
              </h3>
            </div>
          </div>

          <div className="p-8 flex items-center gap-6 group hover:bg-gradient-to-br hover:from-golden/10 hover:to-silver/10 transition-all duration-500">
            <div className="w-14 h-14 bg-gradient-to-br from-golden via-primary to-silver rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <Icon
                name="ShieldCheckIcon"
                size={28}
                className="text-foreground flex-shrink-0" />
            </div>
            <div>
              <div className="text-3xl text-transparent bg-clip-text bg-gradient-to-r from-golden to-silver tracking-tighter font-heading font-light">
                Fair Trade
              </div>
              <h3 className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
                Certified Operations
              </h3>
            </div>
          </div>
        </div>
      </div>
    </section>);

}